import { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../config';
import { subscribePush, unsubscribePush } from '../services/push.service';

const VAPID_PUBLIC_KEY =
  'BPYGlXM70CQsbQPv0gzN7t9d4B-_5vht7DMIrhOHk2mx_GQovr1E-ysowaNNALFXjfYEmOuFVUXqEBZgVIfcu6E';

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

export interface UsePushNotificationsReturn {
  isSupported: boolean;
  isSubscribed: boolean;
  permission: NotificationPermission;
  isLoading: boolean;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!isSupported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {
      setIsSubscribed(false);
    }
  }, [isSupported]);

  useEffect(() => {
    void checkSubscription();
  }, [checkSubscription]);

  // Auto-subscribe if permission already granted but not yet subscribed
  useEffect(() => {
    if (isSupported && permission === 'granted' && !isSubscribed && !isLoading) {
      void subscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported, permission, isSubscribed]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    setIsLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      if (!accessToken) throw new Error('Não autenticado');

      await subscribePush(sub, accessToken);
      setIsSubscribed(true);
      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!isSupported) return;

    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
        if (accessToken) {
          await unsubscribePush(sub.endpoint, accessToken);
        }
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  return { isSupported, isSubscribed, permission, isLoading, subscribe, unsubscribe };
}
